/**
 * KPIs et badges — page « Exécution des passages » (données GET /api/passages).
 */
import type { PassageApiItem } from "../../planification-logistique/planificateur/planningTypes";
import {
  clientLabel,
  effectiveStatutUpper,
} from "../../planification-logistique/planificateur/planningDashboardUtils";
import type { UserRole } from "../../../context/AuthContext";

export type ExecutionKpis = {
  /** À traiter au sens « pas encore terminé terrain » : retards + créneaux du jour (hors futurs, hors attente validation). */
  passagesAExecuter: number;
  enRetard: number;
  aujourdhui: number;
  enCours: number;
  enAttenteValidation: number;
  valides: number;
  suspectsGps: number;
  facturables: number;
};

export type GpsBadgeKind = "ok" | "suspect" | "none";

export function gpsBadgeKind(p: PassageApiItem): GpsBadgeKind {
  if (p.isSuspect) return "suspect";
  if (p.isGpsValid) return "ok";
  const hasAnyCoord =
    p.startLatitude != null ||
    p.startLongitude != null ||
    p.endLatitude != null ||
    p.endLongitude != null;
  if (!hasAnyCoord) return "none";
  return "suspect";
}

export function photoFraction(p: PassageApiItem): { label: string; n: number } {
  const preuves = p.preuves ?? [];
  const avantUrl = typeof p.beforePhotoUrl === "string" && p.beforePhotoUrl.trim().length > 0;
  const apresUrl = typeof p.afterPhotoUrl === "string" && p.afterPhotoUrl.trim().length > 0;
  const avant =
    avantUrl ||
    Boolean(p.isPhotoAvantValid) ||
    preuves.some((x) => String(x.type).toUpperCase() === "AVANT");
  const apres =
    apresUrl ||
    Boolean(p.isPhotoApresValid) ||
    preuves.some((x) => String(x.type).toUpperCase() === "APRES");
  const n = (avant ? 1 : 0) + (apres ? 1 : 0);
  const label =
    n === 0 ? "0/2 — Photos manquantes" : n === 1 ? "1/2 — Photo incomplète" : "2/2 — Photos complètes";
  return { label, n };
}

export type ValidationUi = "en_attente" | "valide" | "rejete" | "terrain" | "clos_anomalies";

export function validationUi(p: PassageApiItem): ValidationUi {
  const ds = (p.displayStatus ?? "").toUpperCase();
  if (ds === "VALIDATED_CLOSED" || ds === "VALIDATED") return "valide";
  if (ds === "REJECTED_CLOSED" || ds === "REJECTED") return "rejete";
  if (ds === "CLOSED_WITH_ANOMALIES") return "clos_anomalies";
  if (ds === "PENDING_VALIDATION") return "en_attente";

  const raw = (p.statut ?? "").toUpperCase();
  const s = effectiveStatutUpper(p);
  if (raw === "CLOSED_WITH_ANOMALIES" || s === "CLOSED_WITH_ANOMALIES") return "clos_anomalies";
  if (s === "EN_ATTENTE_VALIDATION") {
    const te = (p.tournee?.executionStatus ?? "").toUpperCase();
    const ts = (p.tournee?.statut ?? "").toUpperCase();
    if (te === "TERMINEE" || ts === "TERMINEE") return "clos_anomalies";
    return "en_attente";
  }
  if (s === "EFFECTUE" || s === "PARTIEL") return "valide";
  if (s === "NON_EFFECTUE" || s === "ANNULE") return "rejete";
  return "terrain";
}

export function validationLabel(v: ValidationUi): string {
  if (v === "valide") return "Validé";
  if (v === "rejete") return "Non exécuté / refus";
  if (v === "clos_anomalies") return "Clôturé avec anomalies";
  if (v === "en_attente") return "Validation requise";
  return "—";
}

/** Sous-texte qualité / facturation pour badge validation. */
export function validationQualiteSubtitle(p: PassageApiItem): string | undefined {
  const ds = (p.displayStatus ?? "").toUpperCase();
  const anomalieCloture =
    ds === "CLOSED_WITH_ANOMALIES" || (!p.displayStatus && validationUi(p) === "clos_anomalies");
  if (anomalieCloture && !p.isFacturable) {
    return "Non facturable — preuves/GPS incomplets";
  }
  return undefined;
}

/** Libellé colonne « Validation » : serveur d’abord. */
export function passageValidationColumnLabel(p: PassageApiItem): string {
  const fromApi = p.displayLabel?.trim();
  if (fromApi) return fromApi;
  return validationLabel(validationUi(p));
}

/** Statuts Prisma autorisant un démarrage terrain (hors annulé / clôturé / réalisé / attente validation). */
export function passageTerrainStartAllowedByStatut(p: PassageApiItem): boolean {
  const s = effectiveStatutUpper(p);
  if ((p.executionStatus ?? "").toUpperCase() === "REALISE") return false;
  if (s === "ANNULE" || s === "NON_EFFECTUE") return false;
  if (
    s === "EFFECTUE" ||
    s === "PARTIEL" ||
    s === "EN_ATTENTE_VALIDATION" ||
    s === "CLOSED_WITH_ANOMALIES"
  ) {
    return false;
  }
  return s === "PLANIFIE" || s === "CONFIRME" || s === "REPORTE";
}

/**
 * Démarrer : uniquement retard / aujourd’hui, non bloqué séquence, statuts PLANIFIE/CONFIRME/REPORTE.
 * (Pas les futurs planifiés ni les passages bloqués.)
 */
export function isPassageExecutionStartable(p: PassageApiItem): boolean {
  if (!passageTerrainStartAllowedByStatut(p)) return false;
  if (p.sequenceGuard?.blocked === true || p.sequenceBlocked === true) return false;
  if (p.provisionAssignmentBlocked) return false;
  const op = p.executionOperationalStatus ?? "";
  if (op !== "EN_RETARD_A_EXECUTER" && op !== "AUJOURDHUI") return false;
  return true;
}

/** UI bouton démarrer : actif, futur (libellé désactivé), bloqué provision, ou masqué. */
/** Libellé UX bouton démarrer — priorité à `executionStatus` (sync tournée lancée). */
export function passageExecutionStartUi(
  p: PassageApiItem
): "start" | "not_yet" | "none" | "provision_blocked" {
  const exec = String(p.executionStatus ?? "").toUpperCase();
  if (exec === "EN_COURS" || exec === "REALISE" || exec === "ECHEC") return "none";
  if (p.startedAt) return "none";
  if (!passageTerrainStartAllowedByStatut(p)) return "none";
  if (p.sequenceGuard?.blocked === true || p.sequenceBlocked === true) return "none";
  if (p.provisionAssignmentBlocked) return "provision_blocked";
  const op = p.executionOperationalStatus ?? "";
  if (op === "EN_RETARD_A_EXECUTER" || op === "AUJOURDHUI") return "start";
  if (op === "PLANIFIE" || op === "URGENCE") return "not_yet";
  return "none";
}

export function partitionExecutionPassages(rows: PassageApiItem[]): {
  enCours: PassageApiItem[];
  overdue: PassageApiItem[];
  today: PassageApiItem[];
  enAttenteValidation: PassageApiItem[];
  upcoming: PassageApiItem[];
} {
  const enCours: PassageApiItem[] = [];
  const overdue: PassageApiItem[] = [];
  const today: PassageApiItem[] = [];
  const enAttenteValidation: PassageApiItem[] = [];
  const upcoming: PassageApiItem[] = [];
  for (const p of rows) {
    const exec = String(p.executionStatus ?? "").toUpperCase();
    const op = p.executionOperationalStatus;
    if (exec === "EN_COURS" || op === "EN_COURS") enCours.push(p);
    else if (op === "EN_RETARD_A_EXECUTER") overdue.push(p);
    else if (op === "AUJOURDHUI") today.push(p);
    else if (op === "EN_ATTENTE_VALIDATION") enAttenteValidation.push(p);
    else upcoming.push(p);
  }
  return { enCours, overdue, today, enAttenteValidation, upcoming };
}

export function computeExecutionKpis(rows: PassageApiItem[]): ExecutionKpis {
  const enRetard = rows.filter((p) => p.executionOperationalStatus === "EN_RETARD_A_EXECUTER").length;
  const aujourdhui = rows.filter((p) => p.executionOperationalStatus === "AUJOURDHUI").length;
  const passagesAExecuter = enRetard + aujourdhui;
  const enCours = rows.filter((p) => {
    const exec = String(p.executionStatus ?? "").toUpperCase();
    if (exec === "EN_COURS") return true;
    const s = effectiveStatutUpper(p);
    return s === "EN_COURS" || s === "EN_ROUTE";
  }).length;
  const enAttenteValidation = rows.filter((p) => validationUi(p) === "en_attente").length;
  const valides = rows.filter((p) => {
    const s = effectiveStatutUpper(p);
    return s === "EFFECTUE" || s === "PARTIEL";
  }).length;
  const suspectsGps = rows.filter((p) => Boolean(p.isSuspect)).length;
  const facturables = rows.filter((p) => p.isFacturable === true).length;

  return {
    passagesAExecuter,
    enRetard,
    aujourdhui,
    enCours,
    enAttenteValidation,
    valides,
    suspectsGps,
    facturables,
  };
}

export function userCanValidate(role: UserRole | undefined): boolean {
  if (!role) return false;
  const r = role.toLowerCase();
  return r === "admin" || r === "superviseur" || r === "planificateur";
}

export function filterPassagesBySearch(rows: PassageApiItem[], q: string): PassageApiItem[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((p) => {
    const client = clientLabel(p).toLowerCase();
    const pid = String(p.id);
    const tour = (p.tournee?.numero ?? "").toLowerCase();
    const zone = (p.zone?.nom ?? "").toLowerCase();
    return (
      client.includes(needle) ||
      pid.includes(needle) ||
      tour.includes(needle) ||
      zone.includes(needle)
    );
  });
}
